import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import {
	type OrdersTableRow,
	OrdersTableTab,
	ProductConfiguration,
	type ProgramOwnerServiceConfig,
	Services,
} from '@ekhodealer/ekho-common';
import {
	Button,
	Empty,
	Group,
	Pagination,
	SkeletonRect,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { SortDirection, TableColumn } from '@common/components/Table/Table';

import { FloatingActionMenu } from '@common/components/FloatingActionMenu';
import NoOrders from '@common/components/icons/NoOrders';
import { SelectAllBanner } from '@common/components/SelectAllBanner';
import {Table } from '@common/components/Table/Table';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { PLACEHOLDER } from '@common/lib/strings';

import { useExportOrders } from '../api/useExportOrders';
import { useSearchOrders } from '../api/useSearchOrders';
import { activeFiltersCountAtom } from '../atoms/ordersAppliedFiltersAtoms';
import {
	clearAllFiltersAtom,
	currentPageAtom,
	isAllMatchingOrdersSelectedAtom,
	itemsPerPageAtom,
	ordersParamsAtom,
	orderTabFilterAtom,
	selectedOrdersAtom,
	sortConfigAtom,
	updateCurrentOrdersListAtom,
	updateItemsPerPageAtom,
	updateSelectedOrdersAtom,
	updateSortAtom,
} from '../atoms/ordersAtoms';
import { OrdersTableColumnId } from '../constants/ordersTableColumns';
import { useOrderNavigation } from '../hooks/useOrderNavigation';

import { OrderActionMenu } from './OrderActionMenu';
import { OrderDetailsDrawer } from './OrderDetailsDrawer';
import {
	BuyerCell,
	CancelationReasonCell,
	DmvWorkflowCell,
	EstFulfillmentDateCell,
	FinanceAndInsuranceCell,
	FinanceAndInsuranceProfitCell,
	FinancingCell,
	FulfillmentTypeCell,
	OrderDateCell,
	OrderIdCell,
	OrderTabCell,
	PartsGarmentsAndAccessoriesCell,
	PaymentCell,
	PaymentChoiceCell,
	RegistrationStateCell,
	TasksCell,
	TotalCell,
	VehicleCell,
	VinCell,
} from './table-cells';

// Centralized cell rendering function
const renderCell = (
	columnId: OrdersTableColumnId,
	item: OrdersTableRow,
	isLoading: boolean,
): React.ReactNode => {
	if (isLoading) {
		return <SkeletonRect width={100} height={24} />;
	}

	switch (columnId) {
		case OrdersTableColumnId.ORDER_ID:
			return <OrderIdCell orderId={item.orderId} orderNumber={item.orderNumber} />;
		case OrdersTableColumnId.BUYER:
			return (
				<BuyerCell
					buyerName={item.buyer?.fullName || PLACEHOLDER}
					purchasedVehicleId={item.purchasedVehicleId}
				/>
			);
		case OrdersTableColumnId.ORDERS_TABLE_TAB:
			return <OrderTabCell tab={item.tabLabel} />;
		case OrdersTableColumnId.CANCELATION_REASON:
			return <CancelationReasonCell cancelationReason={item.cancelationReason} />;
		case OrdersTableColumnId.ORDER_DATE:
			return <OrderDateCell date={item.date} />;
		case OrdersTableColumnId.VEHICLE:
			return (
				<VehicleCell
					vehicleName={item.vehicle?.name || PLACEHOLDER}
					purchasedVehicleId={item.purchasedVehicleId}
				/>
			);
		case OrdersTableColumnId.PGA:
			return (
				<PartsGarmentsAndAccessoriesCell
					displayText={
						item.partsGarmentsAndAccessories?.length
							? `${item.partsGarmentsAndAccessories.length} items`
							: undefined
					}
					purchasedVehicleId={item.purchasedVehicleId}
					itemsCount={item.partsGarmentsAndAccessories?.length}
				/>
			);
		case OrdersTableColumnId.FI:
			return (
				<FinanceAndInsuranceCell
					displayText={
						item.financeAndInsurance ? `${item.financeAndInsurance} item(s)` : undefined
					}
					purchasedVehicleId={item.purchasedVehicleId}
					itemsCount={item.financeAndInsurance}
				/>
			);
		case OrdersTableColumnId.FI_PROFIT:
			return (
				<FinanceAndInsuranceProfitCell
					profit={item.financeAndInsuranceProfitPerRetailUnit}
				/>
			);
		case OrdersTableColumnId.TOTAL:
			return <TotalCell total={item.total} />;
		case OrdersTableColumnId.PAYMENT_CHOICE:
			return <PaymentChoiceCell paymentChoice={item.paymentChoice} />;
		case OrdersTableColumnId.PAYMENT:
			return (
				<PaymentCell
					status={item.payment?.status}
					purchasedVehicleId={item.purchasedVehicleId}
				/>
			);
		case OrdersTableColumnId.FINANCING:
			return (
				<FinancingCell
					status={item.financing?.status}
					purchasedVehicleId={item.purchasedVehicleId}
				/>
			);
		case OrdersTableColumnId.REGISTRATION_STATE:
			return <RegistrationStateCell registrationState={item.registrationState} />;
		case OrdersTableColumnId.DMV_WORKFLOW:
			return <DmvWorkflowCell dmvWorkflow={item.dmvWorkflow} />;
		case OrdersTableColumnId.VIN:
			return <VinCell vin={item.vin} />;
		case OrdersTableColumnId.FULFILLMENT_TYPE:
			return <FulfillmentTypeCell fulfillmentType={item.fulfillmentType} />;
		case OrdersTableColumnId.EST_FULFILLMENT_DATE:
			return <EstFulfillmentDateCell estFulfillmentDate={item.estFulfillmentDate} />;
		case OrdersTableColumnId.TASKS:
			return <TasksCell item={item} />;
		case OrdersTableColumnId.ACTIONS:
			return <OrderActionMenu item={item} />;
		default:
			return <Text size='bodySmall'>—</Text>;
	}
};

export const OrdersTable = () => {
	// Get filter params from atoms
	const params = useAtomValue(ordersParamsAtom);

	// Current tab state
	const currentTab = useAtomValue(orderTabFilterAtom);

	// Product config — used to hide F&I columns for Access clients
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programOwnerConfig = servicesProvider?.config?.[Services.PROGRAM_OWNER] as
		| ProgramOwnerServiceConfig
		| undefined;
	const isAccessProduct = programOwnerConfig?.product === ProductConfiguration.ACCESS;

	// Pagination state
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const updateItemsPerPage = useSetAtom(updateItemsPerPageAtom);

	// Selection state
	const selectedOrders = useAtomValue(selectedOrdersAtom);
	const updateSelectedOrders = useSetAtom(updateSelectedOrdersAtom);
	const [isAllMatchingSelected, setIsAllMatchingSelected] = useAtom(
		isAllMatchingOrdersSelectedAtom,
	);

	// Sort state
	const sortConfig = useAtomValue(sortConfigAtom);
	const updateSort = useSetAtom(updateSortAtom);

	// Navigation
	const { openOrderDetails } = useOrderNavigation();

	// Clear all filters
	const clearAllFilters = useSetAtom(clearAllFiltersAtom);
	const activeFiltersCount = useAtomValue(activeFiltersCountAtom);
	const hasFiltersApplied = activeFiltersCount > 0;

	// React query hook to get orders data with filters
	const { data, isLoading } = useSearchOrders(params);
	const items = useMemo(() => data?.data ?? [], [data?.data]);
	const queryCount = data?.count ?? 0;

	const isAllPageSelected = selectedOrders === 'all';
	const selectedOrdersCount = isAllMatchingSelected
		? queryCount
		: isAllPageSelected
			? items.length
			: (selectedOrders as Set<string>).size;

	// Handle exporting
	const [loadingExport, setLoadingExport] = useState(false);
	const exportOrdersMutation = useExportOrders();

	// Sync current orders list for navigation
	const updateCurrentOrdersList = useSetAtom(updateCurrentOrdersListAtom);
	useEffect(() => {
		if (!isLoading && items.length > 0) {
			updateCurrentOrdersList(items.map((item) => item.orderId));
		}
	}, [items, isLoading, updateCurrentOrdersList]);

	// Helper function to get sort direction for a specific column
	const getSortDirection = (columnId: string): SortDirection => {
		const sort = sortConfig.find((s) => s.column === columnId);
		return sort?.direction || 'none';
	};

	// Handle sort changes
	const handleSort = (columnId: string, direction: SortDirection) => {
		updateSort({ columnId, direction });
	};

	// State update handlers
	const handleItemsPerPageChange = (newItemsPerPage: number) => {
		updateItemsPerPage(newItemsPerPage);
	};
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};
	const handleRowActivate = (item: OrdersTableRow) => {
		openOrderDetails(item.orderId);
	};

	const columns: TableColumn<OrdersTableRow>[] = [
		{
			id: OrdersTableColumnId.ORDER_ID,
			title: 'Order #',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.ORDER_ID),
			onSort: (direction) => handleSort(OrdersTableColumnId.ORDER_ID, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.ORDER_ID, item, isLoading),
		},
		{
			id: OrdersTableColumnId.BUYER,
			title: 'Buyer',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.BUYER),
			onSort: (direction) => handleSort(OrdersTableColumnId.BUYER, direction),
			isButtonCell: true,
			renderCell: (item) => renderCell(OrdersTableColumnId.BUYER, item, isLoading),
		},
		{
			id: OrdersTableColumnId.ORDERS_TABLE_TAB,
			title: 'Order stage',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.ORDERS_TABLE_TAB),
			onSort: (direction) => handleSort(OrdersTableColumnId.ORDERS_TABLE_TAB, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.ORDERS_TABLE_TAB, item, isLoading),
		},
		...(currentTab === OrdersTableTab.CANCELED
			? [
					{
						id: OrdersTableColumnId.CANCELATION_REASON,
						title: 'Cancelation reason',
						sortable: false,
						sortDirection: getSortDirection(OrdersTableColumnId.CANCELATION_REASON),
						onSort: (direction: SortDirection) =>
							handleSort(OrdersTableColumnId.CANCELATION_REASON, direction),
						renderCell: (item: OrdersTableRow) =>
							renderCell(OrdersTableColumnId.CANCELATION_REASON, item, isLoading),
					} as TableColumn<OrdersTableRow>,
				]
			: []),
		{
			id: OrdersTableColumnId.ORDER_DATE,
			title: 'Order date',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.ORDER_DATE),
			onSort: (direction) => handleSort(OrdersTableColumnId.ORDER_DATE, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.ORDER_DATE, item, isLoading),
		},
		{
			id: OrdersTableColumnId.VEHICLE,
			title: 'Vehicle',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.VEHICLE),
			onSort: (direction) => handleSort(OrdersTableColumnId.VEHICLE, direction),
			isButtonCell: true,
			renderCell: (item) => renderCell(OrdersTableColumnId.VEHICLE, item, isLoading),
		},
		{
			id: OrdersTableColumnId.PGA,
			title: 'PG&A',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.PGA),
			onSort: (direction) => handleSort(OrdersTableColumnId.PGA, direction),
			isButtonCell: true,
			renderCell: (item) => renderCell(OrdersTableColumnId.PGA, item, isLoading),
		},
		...(!isAccessProduct
			? [
					{
						id: OrdersTableColumnId.FI,
						title: 'F&I',
						sortable: false, // TODO: Add indexes
						sortDirection: getSortDirection(OrdersTableColumnId.FI),
						onSort: (direction: SortDirection) =>
							handleSort(OrdersTableColumnId.FI, direction),
						isButtonCell: true,
						renderCell: (item: OrdersTableRow) =>
							renderCell(OrdersTableColumnId.FI, item, isLoading),
					} as TableColumn<OrdersTableRow>,
					{
						id: OrdersTableColumnId.FI_PROFIT,
						title: 'F&I PRU',
						sortable: false, // TODO: Add indexes
						sortDirection: getSortDirection(OrdersTableColumnId.FI_PROFIT),
						onSort: (direction: SortDirection) =>
							handleSort(OrdersTableColumnId.FI_PROFIT, direction),
						renderCell: (item: OrdersTableRow) =>
							renderCell(OrdersTableColumnId.FI_PROFIT, item, isLoading),
					} as TableColumn<OrdersTableRow>,
				]
			: []),
		{
			id: OrdersTableColumnId.TOTAL,
			title: 'Total',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.TOTAL),
			onSort: (direction) => handleSort(OrdersTableColumnId.TOTAL, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.TOTAL, item, isLoading),
		},
		{
			id: OrdersTableColumnId.PAYMENT_CHOICE,
			title: 'Payment choice',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.PAYMENT_CHOICE),
			onSort: (direction) => handleSort(OrdersTableColumnId.PAYMENT_CHOICE, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.PAYMENT_CHOICE, item, isLoading),
		},
		{
			id: OrdersTableColumnId.PAYMENT,
			title: 'Payment status',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.PAYMENT),
			onSort: (direction) => handleSort(OrdersTableColumnId.PAYMENT, direction),
			isButtonCell: true,
			renderCell: (item) => renderCell(OrdersTableColumnId.PAYMENT, item, isLoading),
		},
		{
			id: OrdersTableColumnId.FINANCING,
			title: 'Financing status',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.FINANCING),
			onSort: (direction) => handleSort(OrdersTableColumnId.FINANCING, direction),
			isButtonCell: true,
			renderCell: (item) => renderCell(OrdersTableColumnId.FINANCING, item, isLoading),
		},
		{
			id: OrdersTableColumnId.REGISTRATION_STATE,
			title: 'Reg. state',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.REGISTRATION_STATE),
			onSort: (direction) => handleSort(OrdersTableColumnId.REGISTRATION_STATE, direction),
			renderCell: (item) =>
				renderCell(OrdersTableColumnId.REGISTRATION_STATE, item, isLoading),
		},
		{
			id: OrdersTableColumnId.DMV_WORKFLOW,
			title: 'DMV workflow',
			sortable: false,
			sortDirection: getSortDirection(OrdersTableColumnId.DMV_WORKFLOW),
			onSort: (direction) => handleSort(OrdersTableColumnId.DMV_WORKFLOW, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.DMV_WORKFLOW, item, isLoading),
		},
		{
			id: OrdersTableColumnId.VIN,
			title: 'VIN',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.VIN),
			onSort: (direction) => handleSort(OrdersTableColumnId.VIN, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.VIN, item, isLoading),
		},
		{
			id: OrdersTableColumnId.FULFILLMENT_TYPE,
			title: 'Fulfillment type',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.FULFILLMENT_TYPE),
			onSort: (direction) => handleSort(OrdersTableColumnId.FULFILLMENT_TYPE, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.FULFILLMENT_TYPE, item, isLoading),
		},
		{
			id: OrdersTableColumnId.EST_FULFILLMENT_DATE,
			title: 'Est. fulfillment date',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.EST_FULFILLMENT_DATE),
			onSort: (direction) => handleSort(OrdersTableColumnId.EST_FULFILLMENT_DATE, direction),
			renderCell: (item) =>
				renderCell(OrdersTableColumnId.EST_FULFILLMENT_DATE, item, isLoading),
		},
		{
			id: OrdersTableColumnId.TASKS,
			title: 'Tasks',
			sortable: true,
			sortDirection: getSortDirection(OrdersTableColumnId.TASKS),
			onSort: (direction) => handleSort(OrdersTableColumnId.TASKS, direction),
			renderCell: (item) => renderCell(OrdersTableColumnId.TASKS, item, isLoading),
		},
		{
			id: OrdersTableColumnId.ACTIONS,
			title: '',
			sortable: false,
			renderCell: (item) => renderCell(OrdersTableColumnId.ACTIONS, item, isLoading),
			renderHeader: () => null, // No header content
		},
	];

	async function handleExportSelectedAsCsv() {
		setLoadingExport(true);
		let purchasedVehicleIds: string[] | undefined;

		if (isAllMatchingSelected) {
			purchasedVehicleIds = undefined;
		} else if (selectedOrders === 'all') {
			purchasedVehicleIds = items.map((item) => item.purchasedVehicleId);
		} else if (selectedOrders instanceof Set) {
			// Map selected orderIds to purchasedVehicleIds
			const selectedOrderIds = Array.from(selectedOrders).map((id) => String(id));
			purchasedVehicleIds = items
				.filter((item) => selectedOrderIds.includes(item.orderId))
				.map((item) => item.purchasedVehicleId);
		}

		try {
			await exportOrdersMutation.mutateAsync({
				purchasedVehicleIds,
			});

			// Show success toast
			toast({
				message: `${selectedOrdersCount} orders exported`,
				variant: 'success',
			});
		} catch {
			// Error is handled by the mutation, but we can still show an error toast
			toast({
				message: 'Failed to export orders. Please try again.',
				variant: 'error',
			});
		}

		setLoadingExport(false);
	}

	// Placeholder for skeleton items when loading
	const getDisplayItems = () => {
		if (isLoading) {
			return Array.from({ length: itemsPerPage }, (_, index) => ({
				orderId: `skeleton-${index}`,
				purchasedVehicleId: `skeleton-${index}`,
				buyer: {
					fullName: '',
					phone: '',
					email: '',
					address: '',
					riskScore: 0,
				},
				stage: '',
				date: '',
				vehicle: {
					thumbnailUrl: '',
					modelName: '',
					color: '',
					type: '',
					year: 0,
					source: 'inventory',
				},
				partsGarmentsAndAccessories: [],
				financeAndInsurance: 0,
				financeAndInsuranceProfitPerRetailUnit: '',
				total: '',
				payment: {
					status: '',
					amountPaid: 0,
					amountOutstanding: 0,
					paymentReceipts: [],
				},
				paymentChoice: 'Financing',
				financing: {
					status: '',
					application: {
						ficoScore: 0,
						requestedTerms: '',
					},
					submissions: [],
				},
				registrationState: '',
				vin: '',
				fulfillmentType: '',
				estFulfillmentDate: '',
				numberOfTasks: 0,
			})) as unknown as OrdersTableRow[];
		}

		return items;
	};

	// Check if we have empty results (not loading, no data)
	const hasEmptyResults = !isLoading && items.length === 0;

	// Show empty state when there are no results
	if (hasEmptyResults) {
		return (
			<Empty
				icon={<NoOrders />}
				title={hasFiltersApplied ? 'No orders match your filters' : 'No orders yet'}
				description={
					hasFiltersApplied
						? 'Try adjusting or clearing your filters to see more results.'
						: "When you start receiving orders, they'll show up here. Get ready to track, fulfill, and manage everything in one place."
				}
				content={
					hasFiltersApplied ? (
						<Button variant='secondary' size='small' onPress={() => clearAllFilters()}>
							Clear all
						</Button>
					) : undefined
				}
			/>
		);
	}

	return (
		<>
			<Table<OrdersTableRow>
				columns={columns}
				items={getDisplayItems()}
				getItemId={(item) => item.purchasedVehicleId}
				selectionMode='multiple'
				selectedItems={selectedOrders}
				onSelectionChange={updateSelectedOrders}
				onRowActivate={handleRowActivate}
				aria-label={isLoading ? 'Loading orders table' : 'Orders table'}
				stickyRightCount={2}
				belowTableContent={
					!hasEmptyResults &&
					!isLoading && (
						<Pagination
							variant='contained'
							currentPage={currentPage}
							itemsPerPage={itemsPerPage}
							totalItems={queryCount}
							itemsPerPageOptions={[10, 25, 50, 100]}
							onNavigateTo={handlePageChange}
							onItemsPerPageChange={handleItemsPerPageChange}
							aria-label='Orders table pagination'
						/>
					)
				}
			/>
			<FloatingActionMenu
				isOpen={selectedOrdersCount > 0}
				onClose={() => {
					updateSelectedOrders(new Set());
				}}
				closeButtonTooltip='Deselect all'
				leftContent={
					<div style={{ marginRight: 'auto' }}>
						<SelectAllBanner
							selectedCount={
								isAllPageSelected
									? items.length
									: (selectedOrders as Set<string>).size
							}
							totalCount={queryCount}
							isAllPageSelected={isAllPageSelected}
							isAllMatchingSelected={isAllMatchingSelected}
							onSelectAllMatching={() => setIsAllMatchingSelected(true)}
							onClearSelection={() => updateSelectedOrders(new Set())}
							resourceName='orders'
						/>
					</div>
				}
				rightContent={
					<Group noWrap itemsSpacing='8'>
						<Button
							size='medium'
							variant='secondary'
							rounding='10'
							isLoading={loadingExport}
							onPress={handleExportSelectedAsCsv}>
							Export
						</Button>
					</Group>
				}
			/>
			<OrderDetailsDrawer />
		</>
	);
};
